<template>
	<CommonLoadingState
		v-if="isAthletesLoading"
		:data-type="'athletes'"
	/>
	<div
		v-else-if="!athletes?.length"
		class="text-center py-8 py-lg-16"
	>
		<CommonEmptyState />
	</div>
	<template v-else>
		<SubLayoutVirtualTile
			:length="athletes.length"
			:page-provider="pageProvider"
			:page-size="pageSize"
		>
			<template #probe>
				<AthleteTileItem />
			</template>
			<template #placeholder>
				<VSheet
					height="100%"
					width="100%"
					position="absolute"
					color="background"
					rounded
					:elevation="1"
				/>
			</template>
			<template #default="{ item }">
				<AthleteTileItem
					:athlete="item"
					@work-with-me="workWithMe"
				/>
			</template>
		</SubLayoutVirtualTile>
		<div class="text-center pt-6 pb-12">
			<VBtnSchool
				class="text-decoration-none"
				data-testid="buttonRegister"
				@click="openRegisterNowDialog"
			>
				{{ t('registerToViewMore') }}
			</VBtnSchool>
		</div>
	</template>
</template>

<script setup lang="ts">
import { onBeforeMount, watch } from 'vue';

import { useExchangeSchoolSiteService } from '@/api/exchangeSchoolSiteService';
import { type SingleExchangeSchoolSiteAthlete } from '@/types/exchangeSchoolSite';
import { useExchangeAthletesStore } from '@/stores/exchangeAthletesStore';
import { storeToRefs } from 'pinia';
import CommonEmptyState from '@/components/CommonEmptyState.vue';
import CommonLoadingState from '@/components/CommonLoadingState.vue';
import { useRoute } from 'vue-router';
import SubLayoutVirtualTile from '@/components/SubLayoutVirtualTile.vue';
import AthleteTileItem from '@/components/exchange-school-site/AthleteTileItem.vue';
import { useExchangeSchoolSiteRegisterNowDialog } from '@/composables/useExchangeSchoolSiteRegisterNowDialog';
import { useVirtualization } from '@/composables/useVirtualization';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const length = defineModel<number>('length');

const emit = defineEmits<{
	(e: 'work-with-me', payload: SingleExchangeSchoolSiteAthlete): void;
}>();

const route = useRoute();

const exchangeAthletesStore = useExchangeAthletesStore();
const { athletesRequestParams } = storeToRefs(exchangeAthletesStore);

const workWithMe = (athlete: SingleExchangeSchoolSiteAthlete) => {
	emit('work-with-me', athlete);
};

const {
	items: athletes,
	isItemsLoading: isAthletesLoading,
	getItems,
	length: athletesListTotal,
	pageProvider,
	pageSize,
} = useVirtualization<SingleExchangeSchoolSiteAthlete>({
	service: () => useExchangeSchoolSiteService(route.params.schoolSlug as string | undefined),
	requestKey: 'getAthletes',
	params: athletesRequestParams,
});

watch(athletesListTotal, (newVal) => {
	length.value = newVal;
});

const { open: openRegisterNowDialog } = useExchangeSchoolSiteRegisterNowDialog();

onBeforeMount(() => {
	getItems();
});

</script>
```
